import { useState, type ReactNode } from 'react';
import type { ClientState, GameClient } from '../net/client.js';
import { SeatDot } from '../components/common.js';

const COLORS = ['#c0392b', '#2471a3', '#1e8449', '#b7950b', '#6c3483', '#117864'];

export function Lobby({ state, client }: { state: ClientState; client: GameClient }): ReactNode {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [color, setColor] = useState(COLORS[0]!);

  // --- not in a room yet ------------------------------------------------
  if (!state.gameId) {
    return (
      <div className="stack">
        <div className="card">
          <h1>Risk Bookkeeper</h1>
          <p className="muted" style={{ marginTop: 0 }}>
            Keeps score for a game played on a real board. It does the arithmetic,
            tracks the cards and settles the dice, so the table can get on with it.
          </p>
          <button className="primary" style={{ width: '100%' }} onClick={() => client.createRoom()}>
            Start a new game
          </button>
        </div>

        <div className="card">
          <h2>Join a game</h2>
          <div className="row">
            <input
              className="grow mono"
              placeholder="CODE"
              value={code}
              maxLength={6}
              autoCapitalize="characters"
              autoCorrect="off"
              onChange={(e) => setCode(e.target.value.toUpperCase())}
            />
            <button onClick={() => client.joinRoom(code)} disabled={code.trim().length < 4}>
              Join
            </button>
          </div>
        </div>
      </div>
    );
  }

  const seats = state.game.seats;
  const taken = new Set(seats.map((seat) => seat.color));
  const me = seats.find((seat) => seat.id === state.seatId);
  const isHost = me?.isHost === true;

  return (
    <div className="stack">
      <div className="card">
        <h3>Room code</h3>
        <div className="code">{state.roomCode}</div>
        <p className="small muted" style={{ margin: 0 }}>
          Read this out. Everyone else opens the same address and joins with it.
        </p>
      </div>

      <div className="card">
        <h2>Players</h2>
        {seats.length === 0 && <p className="muted small">Nobody has sat down yet.</p>}
        <div className="stack">
          {seats.map((seat) => (
            <div className="row" key={seat.id}>
              <SeatDot color={seat.color} />
              <span className="grow truncate">{seat.name}</span>
              {seat.isHost && <span className="pill">host</span>}
              {seat.id === state.seatId && <span className="pill">you</span>}
            </div>
          ))}
        </div>
      </div>

      {!me && (
        <div className="card">
          <h2>Take a seat</h2>
          <div className="stack">
            <input
              placeholder="Your name"
              value={name}
              maxLength={24}
              onChange={(e) => setName(e.target.value)}
            />
            <div className="row wrap">
              {COLORS.map((option) => (
                <button
                  key={option}
                  aria-label={`colour ${option}`}
                  className={color === option ? 'primary' : ''}
                  disabled={taken.has(option)}
                  onClick={() => setColor(option)}
                  style={{ background: taken.has(option) ? undefined : option, minWidth: 48 }}
                >
                  {color === option ? '✓' : ''}
                </button>
              ))}
            </div>
            <button
              className="primary"
              disabled={name.trim().length === 0}
              onClick={() => client.claimSeat(name.trim(), color)}
            >
              Sit down
            </button>
          </div>
        </div>
      )}

      {me && (
        <div className="card">
          {isHost ? (
            <>
              <h2>Ready when you are</h2>
              <p className="small muted" style={{ marginTop: 0 }}>
                Set the board up as you normally would. You will tell the app who
                holds what in a moment.
              </p>
              <button
                className="primary"
                style={{ width: '100%' }}
                disabled={seats.length < 2}
                onClick={() => client.act({ type: 'startGame' })}
              >
                {seats.length < 2 ? 'Waiting for another player' : 'Start the game'}
              </button>
            </>
          ) : (
            <p className="muted" style={{ margin: 0 }}>
              Waiting for {seats.find((s) => s.isHost)?.name ?? 'the host'} to start.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
